import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tab, Tabs } from '@mui/material';
import { OrderInfo, OrderInfoType, PackageImage, TakeImagePhase } from 'adoms-common-lib';
import React, { useEffect } from "react";
import { imageResizeAndConvertBase64 } from '../../common/ImageEditUtil';
import { APIConnector } from '../../connector/APIConnector';

type OrderImageDialogProps = {
    isOrderImageDialogOpen: boolean
    setOrderImageDialogOpen: () => void
    displayOrderInfo: OrderInfo
    prefetchedPhaseImageState?: Record<TakeImagePhase, PackageImage[]>
    isPrefetchingMaritimeImages?: boolean
    maritimeImageErrorMessage?: string
    onPackageImageUpdated?: () => void
}

/**
 * 注文内容画像表示ダイアログ
 * @param props 
 * @returns 
 */
export const OrderImageDialog: React.FC<OrderImageDialogProps> = (props) => {

    const MAX_PACKAGE_IMAGE_COUNT_PER_PHASE = 3;

    const TAKE_IMAGE_PHASE_ITEMS: { phase: TakeImagePhase, label: string }[] = [
        { phase: TakeImagePhase.Received, label: '受領時' },
        { phase: TakeImagePhase.Packed, label: '梱包時' },
        { phase: TakeImagePhase.Loaded, label: '搭載時' },
    ];

    const createEmptyPhaseImageState = (): Record<TakeImagePhase, PackageImage[]> => ({
        [TakeImagePhase.Received]: [],
        [TakeImagePhase.Packed]: [],
        [TakeImagePhase.Loaded]: []
    });

    const isMaritimeOrder = props.displayOrderInfo?.type === OrderInfoType.MaritimeOrderInfo;

    const [orderReceiptImageBase64, setOrderReceiptImageBase64] = React.useState<string>('');
    const [selectedPhase, setSelectedPhase] = React.useState<TakeImagePhase>(TakeImagePhase.Received);
    const [phaseImageState, setPhaseImageState] = React.useState<Record<TakeImagePhase, PackageImage[]>>(createEmptyPhaseImageState());
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
    const [displayCircularProgress, setDisplayCircularProgress] = React.useState(false);
    const [deletingPathName, setDeletingPathName] = React.useState<string>('');
    const [isUploadingPackageImages, setIsUploadingPackageImages] = React.useState<boolean>(false);
    const [pendingDelete, setPendingDelete] = React.useState<{ phase: TakeImagePhase, image: PackageImage } | undefined>(undefined);
    const displayErrorMessage = isMaritimeOrder ? (errorMessage ?? props.maritimeImageErrorMessage) : errorMessage;
    const isProcessingMaritimeImages = (props.isPrefetchingMaritimeImages === true) || isUploadingPackageImages;
    const isMobile = /iPhone|Android.+Mobile/.test(navigator.userAgent);

    useEffect(() => {
        if (!props.isOrderImageDialogOpen) {
            return;
        }

        setErrorMessage(undefined);
        setDisplayCircularProgress(false);
        setOrderReceiptImageBase64('');
        setSelectedPhase(TakeImagePhase.Received);
        setDeletingPathName('');
        setIsUploadingPackageImages(false);
        setPendingDelete(undefined);
    }, [props.isOrderImageDialogOpen, props.displayOrderInfo.orderID]);

    const handleAddPackageImages = async (phase: TakeImagePhase, event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            if (!event.target.files || event.target.files.length === 0) {
                return;
            }

            setErrorMessage(undefined);
            const currentImageCount = (phaseImageState[phase] ?? []).length;
            const selectedFiles = Array.from(event.target.files);

            if (currentImageCount >= MAX_PACKAGE_IMAGE_COUNT_PER_PHASE) {
                setErrorMessage(`画像は1フェーズあたり最大${MAX_PACKAGE_IMAGE_COUNT_PER_PHASE}枚まで登録できます。`);
                event.target.value = '';
                return;
            }

            if (currentImageCount + selectedFiles.length > MAX_PACKAGE_IMAGE_COUNT_PER_PHASE) {
                setErrorMessage(`画像は1フェーズあたり最大${MAX_PACKAGE_IMAGE_COUNT_PER_PHASE}枚まで登録できます。`);
                event.target.value = '';
                return;
            }

            if (!selectedFiles.every(file => file.type === 'image/jpeg' || file.type === 'image/png')) {
                setErrorMessage('このファイルは送信できません。jpg/png形式の画像ファイルのみ送信可能です。');
                return;
            }

            setIsUploadingPackageImages(true);
            const resizedImageList = await Promise.all(selectedFiles.map(file => imageResizeAndConvertBase64(file)));
            const imageBase64List = resizedImageList.map(image => image.imageBase64);

            const apiConnector: APIConnector = APIConnector.instance;
            await apiConnector.putPackageImage(undefined, props.displayOrderInfo.orderID, phase, imageBase64List);

            setPhaseImageState(prev => ({
                ...prev,
                [phase]: [
                    ...(prev[phase] ?? []),
                    ...imageBase64List.map((imageBase64) => ({
                        pathName: '',
                        imageBase64,
                    }))
                ]
            }));
            props.onPackageImageUpdated?.();
            event.target.value = '';
        } catch (error) {
            console.log(error);
            setErrorMessage('画像を追加できませんでした。');
        } finally {
            setIsUploadingPackageImages(false);
        }
    };

    useEffect(() => {
        if (!isMaritimeOrder) {
            return;
        }

        setPhaseImageState(props.prefetchedPhaseImageState ?? createEmptyPhaseImageState());
    }, [isMaritimeOrder, props.prefetchedPhaseImageState]);

    useEffect(() => {
        const fetchOrderImage = async () => {
            if (!props.isOrderImageDialogOpen) {
                return;
            }

            const apiConnector: APIConnector = APIConnector.instance;

            if (isMaritimeOrder) {
                return;
            }

            if (props.displayOrderInfo.receiptFileName) {
                setDisplayCircularProgress(true);
                await apiConnector.getReciept(props.displayOrderInfo.orderID, props.displayOrderInfo.receiptFileName).then(response => {
                    setOrderReceiptImageBase64(response);
                    setErrorMessage(undefined);
                    setDisplayCircularProgress(false);
                }).catch(error => {
                    setErrorMessage("注文内容が取得できませんでした。");
                    console.log(error);
                    setDisplayCircularProgress(false);
                });
            }
        };

        fetchOrderImage();
    }, [props.isOrderImageDialogOpen, props.displayOrderInfo.orderID, props.displayOrderInfo.receiptFileName, isMaritimeOrder]);

    const handleDeletePackageImage = async (phase: TakeImagePhase, image: PackageImage) => {
        if (!image.pathName) {
            return;
        }

        try {
            setDeletingPathName(image.pathName);
            setErrorMessage(undefined);

            const apiConnector: APIConnector = APIConnector.instance;
            await apiConnector.deletePackageImage(image.pathName);

            setPhaseImageState(prev => ({
                ...prev,
                [phase]: (prev[phase] ?? []).filter(target => target.pathName !== image.pathName)
            }));
            props.onPackageImageUpdated?.();
        } catch (error) {
            console.log(error);
            setErrorMessage('画像を削除できませんでした。');
        } finally {
            setDeletingPathName('');
        }
    };

    const handleOpenDeleteConfirm = (phase: TakeImagePhase, image: PackageImage) => {
        setPendingDelete({ phase, image });
    };

    const handleCloseDeleteConfirm = () => {
        if (deletingPathName) {
            return;
        }
        setPendingDelete(undefined);
    };

    const handleConfirmDeletePackageImage = async () => {
        if (!pendingDelete) {
            return;
        }

        await handleDeletePackageImage(pendingDelete.phase, pendingDelete.image);
        setPendingDelete(undefined);
    };

    return (
        <React.Fragment>
            <Dialog
                open={props.isOrderImageDialogOpen}
                onClose={props.setOrderImageDialogOpen}
            >
                <DialogTitle id="dialog-title">
                    注文内容
                </DialogTitle>
                <DialogContent
                    sx={{
                        paddingBottom: 0,
                        minWidth: 300,
                        minHeight: 100,
                        textAlign: "center"
                    }}>
                    {displayErrorMessage ?
                        <Alert severity="error" >{displayErrorMessage}</Alert>
                        : undefined
                    }
                    {(isMaritimeOrder ? isProcessingMaritimeImages : displayCircularProgress) ?
                        <Grid item xs={12} textAlign={"center"}>
                            <CircularProgress />
                        </Grid>
                        :
                        <React.Fragment>
                            {isMaritimeOrder ?
                                <Grid container spacing={2} direction="column">
                                    <Grid item>
                                        <Tabs
                                            value={selectedPhase}
                                            onChange={(_, value: TakeImagePhase) => setSelectedPhase(value)}
                                            variant="fullWidth"
                                        >
                                            {TAKE_IMAGE_PHASE_ITEMS.map((phaseItem) => (
                                                <Tab
                                                    key={phaseItem.phase}
                                                    value={phaseItem.phase}
                                                    label={phaseItem.label}
                                                />
                                            ))}
                                        </Tabs>
                                    </Grid>
                                    <Grid item textAlign={'right'}>
                                        <label htmlFor={`btn-upload-${props.displayOrderInfo.orderID}-${selectedPhase}`}>
                                            <input
                                                id={`btn-upload-${props.displayOrderInfo.orderID}-${selectedPhase}`}
                                                name={`btn-upload-${props.displayOrderInfo.orderID}-${selectedPhase}`}
                                                style={{ display: 'none' }}
                                                type="file"
                                                accept="image/jpeg,image/png"
                                                multiple
                                                onChange={(event) => handleAddPackageImages(selectedPhase, event)} />
                                            <Button
                                                component="span"
                                                disabled={
                                                    isProcessingMaritimeImages ||
                                                    deletingPathName !== '' ||
                                                    (phaseImageState[selectedPhase] ?? []).length >= MAX_PACKAGE_IMAGE_COUNT_PER_PHASE
                                                }
                                                color="primary"
                                                variant="outlined"
                                                startIcon={<AddPhotoAlternateIcon />}
                                            >
                                                追加
                                            </Button>
                                        </label>
                                    </Grid>
                                    <Grid item>
                                        {(phaseImageState[selectedPhase] ?? []).length > 0 ?
                                            <Grid container spacing={2} direction="column">
                                                {(phaseImageState[selectedPhase] ?? []).map((image, index) => (
                                                    <Grid item key={`${selectedPhase}-${index}`} textAlign={'center'}>
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                display: 'inline-block',
                                                                width: isMobile ? '100%' : '50%'
                                                            }}
                                                        >
                                                            <img
                                                                src={image.imageBase64}
                                                                style={{ width: '100%', display: 'block' }}
                                                            />
                                                            <IconButton
                                                                disabled={!image.pathName || deletingPathName === image.pathName}
                                                                onClick={() => handleOpenDeleteConfirm(selectedPhase, image)}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 8,
                                                                    right: 8,
                                                                    width: 24,
                                                                    height: 24,
                                                                    p: 0.25,
                                                                    bgcolor: 'grey.500',
                                                                    color: 'common.white',
                                                                    '&:hover': {
                                                                        bgcolor: 'grey.600'
                                                                    },
                                                                    '&.Mui-disabled': {
                                                                        bgcolor: 'grey.300',
                                                                        color: 'common.white'
                                                                    }
                                                                }}
                                                            >
                                                                <CloseIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Box>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                            :
                                            <Alert severity="info">該当フェーズの画像がありません。</Alert>
                                        }
                                    </Grid>
                                </Grid>
                                :
                                orderReceiptImageBase64 ?
                                    <img
                                        id="receiptImage"
                                        src={orderReceiptImageBase64}
                                        style={
                                            navigator.userAgent.match(/iPhone|Android.+Mobile/) ?
                                                { width: '100%' } :
                                                { width: '50%' }
                                        }
                                    />
                                    :
                                    <Alert severity="info">画像がありません。</Alert>
                            }
                        </React.Fragment>
                    }
                </DialogContent>
                <DialogActions>
                    <Button
                        color="primary"
                        onClick={props.setOrderImageDialogOpen}>
                        閉じる
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={pendingDelete != null}
                onClose={handleCloseDeleteConfirm}
            >
                <DialogTitle>画像削除確認</DialogTitle>
                <DialogContent>
                    この画像を削除しますか？
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseDeleteConfirm}
                        disabled={deletingPathName !== ''}
                    >
                        キャンセル
                    </Button>
                    <Button
                        color='error'
                        onClick={handleConfirmDeletePackageImage}
                        disabled={deletingPathName !== ''}
                    >
                        削除
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};